import { useAuthRuntime } from "@frontend/components/auth/auth-runtime"
import { useAnnotationStore } from "@frontend/stores/annotation-store"
import { useHistoryStore } from "@frontend/stores/history-store"
import type { AnnotationSnapshotV1 } from "@shared/lib/history-types"
import { decodeSessionCbor } from "@shared/lib/session-cbor"
import { useQuery } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"

interface ReviewSubmission {
  id: string
  status: string
  user_id: string
  review_comment: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  updated_at: string
}

interface UseReviewBootstrapParams {
  api: (input: string, init?: RequestInit) => Promise<unknown>
  reviewSubmissionId: string
  caseId?: string
}

export function useReviewBootstrap({ api, reviewSubmissionId, caseId }: UseReviewBootstrapParams) {
  const { getAccessToken } = useAuthRuntime()
  const [editEnabled, setEditEnabled] = useState(false)
  const [viewerSessionState, setViewerSessionState] = useState<{
    pendingRestoreSnapshot: AnnotationSnapshotV1 | null
  }>({
    pendingRestoreSnapshot: null
  })

  const getAuthHeaders = useCallback(async () => {
    const token = await getAccessToken()
    return token ? { Authorization: `Bearer ${token}` } : undefined
  }, [getAccessToken])

  const bootstrapQuery = useQuery<{
    submission: ReviewSubmission
    historyPayload: ArrayBuffer | null
  }>({
    queryKey: ["review-bootstrap", reviewSubmissionId],
    enabled: Boolean(reviewSubmissionId) && Boolean(caseId),
    staleTime: Infinity,
    refetchOnMount: "always",
    queryFn: async ({ signal }) => {
      const submission = (await api(`/submissions/${reviewSubmissionId}`, {
        signal
      })) as ReviewSubmission

      const headers = await getAuthHeaders()
      const historyRes = await fetch(`/api/submissions/${reviewSubmissionId}/history/file`, { headers, signal })

      return {
        submission,
        historyPayload: historyRes.ok ? await historyRes.arrayBuffer() : null
      }
    }
  })

  const isBootstrapping = bootstrapQuery.isPending || bootstrapQuery.isFetching

  // Load session from history payload
  useEffect(() => {
    if (!(caseId && bootstrapQuery.data)) return

    const { submission, historyPayload } = bootstrapQuery.data
    const session = historyPayload ? decodeSessionCbor(historyPayload) : null

    if (session) {
      useHistoryStore.getState().loadSession(session)
      const snapshot = session.checkpoints[session.cursor]?.snapshot
      if (snapshot) {
        useAnnotationStore.getState().replaceFromSnapshot(snapshot)
      }
      setViewerSessionState({
        pendingRestoreSnapshot: snapshot ?? null
      })
      return
    }

    // No history — start with empty state
    useAnnotationStore.getState().reset()
    useHistoryStore.getState().initSession({
      submissionId: submission.id,
      caseId,
      initialSnapshot: useAnnotationStore.getState().getSnapshot()
    })
    setViewerSessionState({ pendingRestoreSnapshot: null })
  }, [bootstrapQuery.data, caseId])

  useEffect(() => {
    if (!bootstrapQuery.isError) return
    useAnnotationStore.getState().reset()
    useHistoryStore.getState().reset()
    setViewerSessionState({ pendingRestoreSnapshot: null })
  }, [bootstrapQuery.isError])

  const applySnapshot = useCallback((snapshot: AnnotationSnapshotV1 | null) => {
    if (!snapshot) return
    useAnnotationStore.getState().replaceFromSnapshot(snapshot)
    setViewerSessionState({ pendingRestoreSnapshot: snapshot })
  }, [])

  const handleTimelineScrub = useCallback(
    (index: number) => {
      applySnapshot(useHistoryStore.getState().seekToCursor(index))
    },
    [applySnapshot]
  )

  return {
    submission: bootstrapQuery.data?.submission ?? null,
    submissionId: reviewSubmissionId,
    editEnabled,
    setEditEnabled,
    isBootstrapping,
    getAuthHeaders,
    applySnapshot,
    handleTimelineScrub,
    pendingRestoreSnapshot: viewerSessionState.pendingRestoreSnapshot,
    clearPendingRestoreSnapshot: () =>
      setViewerSessionState((prev) => ({
        ...prev,
        pendingRestoreSnapshot: null
      })),
    refetchSubmission: bootstrapQuery.refetch
  }
}
